"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, ShoppingBag, ShieldCheck, ClipboardCheck, HardHat, CircleCheck as CheckCircle2, FileText, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accessEnvironments = [{
  id: "town-centre",
  title: "Town-centre commercial & offices",
  iconName: "Building2",
  description: "Carefully managed public protection, pavement licences, and out-of-hours builds for congested commercial streets and multi-storey office blocks.",
  points: "Pavement licences & gantry access, debris netting & fan protection, flexible out-of-hours scheduling",
  photoName: "Basingstoke Commercial Scaffolding Image",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_commercial_scaffold.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_commercial_scaffold.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_commercial_scaffold.webp-wvc-sizes",
  alt: "Commercial scaffolding project in a town centre environment"
}, {
  id: "residential-estates",
  title: "Large residential estates",
  iconName: "Home",
  description: "Reliable access frameworks for roofing replacements, extensions, new-build developments, and domestic solar installations across Basingstoke neighbourhoods.",
  points: "Full perimeter access, chimney & gable scaffolds, solar installer friendly setups",
  photoName: "Basingstoke Residential Scaffolding Image",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_residential_scaffold.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_residential_scaffold.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_residential_scaffold.png-wvc-sizes",
  alt: "Residential scaffolding erected around modern housing"
}, {
  id: "retail-parks",
  title: "Retail parks & logistics hubs",
  iconName: "ShoppingBag",
  description: "Robust structures engineered for active commercial premises, allowing continuous customer footfall, safe deliveries, and ongoing maintenance.",
  points: "Pedestrian tunnels & crash decks, loading bays with safety gates, zero-disruption planning",
  photoName: "Basingstoke Retail Scaffolding Image",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_retail_scaffold.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_retail_scaffold.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/basingstoke_retail_scaffold.png-wvc-sizes",
  alt: "Retail park external maintenance scaffolding"
}];
const safetySteps = [{
  step: "01",
  title: "Site assessment & risk planning",
  description: "Before any tube is unloaded, we inspect ground conditions, underground services, traffic flow, and overhead hazards to produce detailed RAMS."
}, {
  step: "02",
  title: "Safe access & engineered erection",
  description: "Erected by CISRS-qualified scaffolders in strict compliance with TG20 guidance, including internal ladder access towers and secure edge protection."
}, {
  step: "03",
  title: "Handover inspection & tagging",
  description: "A formal handover certificate and Scafftag are issued upon completion, followed by statutory 7-day inspections or re-inspections following adverse weather."
}];
const accreditations = [{
  name: "NASC",
  fullTitle: "National Access & Scaffolding Confederation",
  description: "Full compliance with industry-standard safety practices, audited procedures, and technical guidance."
}, {
  name: "CHAS",
  fullTitle: "Contractors Health and Safety Assessment Scheme",
  description: "Verified health and safety management capability, demonstrating high standards of on-site risk control."
}];
function AccessEnvironmentsCards() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const getPhotoData = (item: (typeof accessEnvironments)[0]) => {
    const found = posts?.find(p => p.title === item.photoName || p.customFields?.photo_name === item.photoName || p.slug === item.photoName.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
    const postImg = found?.customFields?.image || found?.featuredImage;
    const postAlt = found?.customFields?.alt_text || found?.title || item.alt;
    if (postImg) {
      return {
        src: postImg,
        srcset: "",
        sizes: "",
        alt: postAlt
      };
    }
    return {
      src: item.image,
      srcset: item.srcset,
      sizes: item.sizes,
      alt: item.alt
    };
  };
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20">
      {accessEnvironments.map((item, i) => {
      const imgData = getPhotoData(item);
      return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45,
        delay: i * 0.1
      }} className="h-full">
            <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] flex flex-col justify-between overflow-hidden">
              <div>
                <div className="aspect-16/10 w-full overflow-hidden bg-muted relative">
                  {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={imgData.src} data-wvc-srcset={imgData.srcset} data-wvc-sizes={imgData.sizes} alt={imgData.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-xs px-2.5 py-1 rounded-md text-xs font-semibold text-foreground flex items-center gap-1.5 border border-border/80 shadow-2xs">
                    {item.iconName === "Building2" && <Building2 className="size-3.5 text-primary" />}
                    {item.iconName === "Home" && <Home className="size-3.5 text-primary" />}
                    {item.iconName === "ShoppingBag" && <ShoppingBag className="size-3.5 text-primary" />}
                    <span>{item.title}</span>
                  </div>
                </div>
                <CardHeader className="pt-5 pb-3">
                  <CardTitle className="text-lg font-semibold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </div>
              <CardContent className="pt-0 pb-6">
                <Separator className="my-3" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <span className="font-medium text-foreground block mb-1">Key safety controls:</span>
                  <p className="leading-normal">{item.points}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>;
    })}
    </div>;
}
export default function AccessSafety() {
  const wpQuery = new WP_Query({
    post_type: "site_photo",
    per_page: 50
  });
  return <section id="basingstokeaccesssafety" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs">
            Basingstoke & Hampshire Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Access solutions and site safety in Basingstoke
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            We plan, erect, and inspect scaffolding across commercial centres, retail parks, and housing developments with safety and clear compliance at every stage.
          </p>
        </div>

        {/* Access Environments Grid with PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 50,
        status: ["publish"]
      }}>
          <AccessEnvironmentsCards />
        </PostsProvider>

        {/* Safety & Compliance Section */}
        <div className="rounded-2xl bg-muted/40 border border-border p-6 sm:p-8 lg:p-12 mb-16">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase mb-2">
              <ShieldCheck className="size-4" />
              Standard safety protocols
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Rigorous inspection and certified handovers
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Every structure is designed, erected, and signed off under thorough procedural checks to keep contractors, clients, and the public safe.
            </p>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {safetySteps.map((step, i) => <div key={step.step} data-index={i} className="bg-card rounded-xl p-6 border border-border shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                      STEP {step.step}
                    </span>
                    {i === 0 && <ClipboardCheck className="size-5 text-muted-foreground" />}
                    {i === 1 && <HardHat className="size-5 text-muted-foreground" />}
                    {i === 2 && <FileText className="size-5 text-muted-foreground" />}
                  </div>
                  <h4 className="text-base font-semibold text-card-foreground mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Accreditations & CTA Strip */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card rounded-2xl border border-border p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="lg:col-span-7 space-y-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-card-foreground mb-2">
                Confirmed safety accreditations
              </h3>
              <p className="text-sm text-muted-foreground">
                We operate to verified national standards, giving main contractors and property owners complete operational confidence.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="p-4 rounded-lg bg-muted/30 border border-border flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span className="font-bold text-foreground text-sm tracking-wide">
                        {acc.name}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-foreground/80 block mb-1.5">
                      {acc.fullTitle}
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {acc.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center items-start lg:items-end lg:pl-6 lg:border-l lg:border-border gap-4">
            <div className="text-left lg:text-right">
              <span className="text-sm font-semibold text-foreground block">
                Need compliant scaffolding in Basingstoke?
              </span>
              <span className="text-xs text-muted-foreground block mt-1">
                Speak with our team to arrange a site visit or technical access appraisal.
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button asChild size="default" className="w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  Request a quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
                <Link to="/HealthSafetyAccreditationsPage">
                  View safety details
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}